import { Color, Spacing } from '@persona-id/design-tokens';
import {
  BookOpenIcon,
  EditIcon,
  HandIcon,
  MonitorIcon,
  StarIcon,
  ZapIcon,
} from '@persona-id/design-tokens/icons/24px';
import { ReactNode } from 'react';
import styled from 'styled-components';

import { BlueprintBlockType } from 'dashboard/models/blueprint';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import ConfigureIdTypes from 'images/dashboard/onboarding/configure-id-types.gif';
import ConfigureVerificationChecks from 'images/dashboard/onboarding/configure-verification-checks.gif';

type OnboardingGuideModule = {
  moduleKey: OnboardingGuideModuleKey;
  moduleTitle: string;
  moduleIcon: ReactNode;
  steps: OnboardingGuideStep[];
};

export type OnboardingGuideStep = {
  key: OnboardingGuideStepKey;
  title: string;
  customComponentName: string;
  minutesToComplete: number;
  requiredBlueprintBlockType?: BlueprintBlockType;
  content?: ReactNode;
  buttonText?: string;
  buttonClickCompletesStep?: boolean;
  buttonLink?: DashboardPageLink;
};

export enum OnboardingGuideModuleKey {
  Basics = 'basics',
  TestSolution = 'test-solution',
  RefineSolution = 'refine-solution',
  Integration = 'integration',
  ManageUserLifecycle = 'manage-user-lifecycle',
  RequestProductionAccess = 'request-production-access',
}

export enum OnboardingGuideStepKey {
  ChooseSolution = 'choose-solution',
  VideoPreview = 'video-preview',
  InviteTeamMembers = 'invite-team-members',
  PreviewSolution = 'preview-solution',
  RunTestInquiry = 'run-test-inquiry',
  ReviewCompletedInquiry = 'review-completed-inquiry',
  InquiryTemplateBranding = 'inquiry-template-branding',
  InquiryTemplateCountriesIdTypes = 'inquiry-template-countries-id-types',
  VerificationChecks = 'verification-checks',
  ReviewReportResults = 'review-report-results',
  ReviewWorkflowRun = 'review-workflow-run',
  ReviewCase = 'review-case',
  ChooseIntegrationMethod = 'choose-integration-method',
  WebhooksOrApi = 'webhooks-or-api',
  RequestProductionAccess = 'request-production-access',
  RunTestInquiryInProduction = 'run-test-inquiry-in-production',
}

export enum DashboardPageLink {
  Billing = '/dashboard/billing?referrer=home_sandbox',
  Case = '/dashboard/cases/[case-token]?referrer=home_sandbox',
  Inquiry = '/dashboard/inquiries/[inquiry-token]?referrer=home_sandbox',
  InquiryVerifications = '/dashboard/inquiries/[inquiry-token]/verifications?referrer=home_sandbox',
  InquiryTemplateFlow = '/dashboard/inquiry-templates/[inquiry-template-token]/[inquiry-template-version-token]/flow?referrer=home_sandbox',
  InquiryTemplateScreens = '/dashboard/inquiry-templates/[inquiry-template-token]/[inquiry-template-version-token]/screens?referrer=home_sandbox',
  InquiryTemplateVerificationChecks = '/dashboard/inquiry-templates/[inquiry-template-token]/[inquiry-template-version-token]/config/verifications?referrer=home_sandbox',
  IntegrationMethodHostedFlow = '/dashboard/getting-started/hosted-flow?referrer=home_sandbox',
  IntegrationMethodEmbeddedFlow = '/dashboard/getting-started/embedded-flow?referrer=home_sandbox',
  IntegrationMethodMobileSdks = '/dashboard/getting-started/mobile-sdks?referrer=home_sandbox',
  IntegrationMethodServerApis = '/dashboard/getting-started/api?referrer=home_sandbox',
  PreviewInquiry = '[inquiry-preview-link]',
  Report = '/dashboard/reports/[report-token]?referrer=home_sandbox',
  Users = '/dashboard/users?referrer=home_sandbox',
  Webhooks = '/dashboard/webhooks?referrer=home_sandbox',
  Workflow = '/dashboard/workflows/[workflow-token]/[workflow-version-token]?referrer=home_sandbox',
  WorkflowRun = '/dashboard/workflows/[workflow-token]/runs/[workflow-run-token]?referrer=home_sandbox',
}

export const GuideStepTipBanner = styled('div')`
  padding: ${Spacing[2]} ${Spacing[4]};
  background-color: ${Color.CoreSlate100};
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

export const GuideStepWarningBanner = styled('div')`
  padding: ${Spacing[2]} ${Spacing[4]};
  background-color: ${Color.CoreYellow100};
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

export const UnorderedList = styled('ul')`
  margin-left: ${Spacing[5]};
  line-height: ${({ theme }) => theme.text.paragraph.md.lineHeight};
`;

const VideoPlayer = styled('iframe')`
  width: 100%;
  height: 100%;
  min-height: 400px;
`;

const GuideStepGif = styled('img')`
  width: 100%;
`;

export const guideContent: OnboardingGuideModule[] = [
  {
    moduleKey: OnboardingGuideModuleKey.Basics,
    moduleTitle: 'Get to know Persona',
    moduleIcon: <BookOpenIcon />,
    steps: [
      {
        key: OnboardingGuideStepKey.ChooseSolution,
        title: 'Choose a solution',
        customComponentName: 'ChooseSolutionStep',
        minutesToComplete: 2,
      },
      {
        key: OnboardingGuideStepKey.VideoPreview,
        title: 'See what you can do with Persona',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        customComponentName: null,
        minutesToComplete: 2,
        content: (
          <>
            <Paragraph color='primary'>
              <b>Welcome to Persona!</b> We’re excited to help you solve your identity challenges.
            </Paragraph>
            <Spacer height={2} />
            <Paragraph color='primary'>
              Watch this quick video to learn the steps to set up Persona for your business. For a
              better understanding of our full product suite, check out our{' '}
              <a
                href='https://academy.withpersona.com/collections/01916c0c-fcfa-77a3-87e8-494103a807be'
                target='_blank'
                rel='noreferrer'
              >
                Persona Basics course
              </a>{' '}
              at the Persona Academy where we cover all the essential topics.
            </Paragraph>
            <Spacer height={2} />
            <VideoPlayer
              src='https://player.vimeo.com/video/870774593'
              title='Persona Dashboard Overview'
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
            />
          </>
        ),
        buttonText: 'I’ve watched the video!',
        buttonClickCompletesStep: true,
      },
    ],
  },
  {
    moduleKey: OnboardingGuideModuleKey.TestSolution,
    moduleTitle: 'Try out your solution',
    moduleIcon: <HandIcon />,
    steps: [
      {
        key: OnboardingGuideStepKey.PreviewSolution,
        title: 'Preview your Inquiry',
        customComponentName: 'PreviewSolutionStep',
        minutesToComplete: 2,
      },
      {
        key: OnboardingGuideStepKey.RunTestInquiry,
        title: 'Create and try out an Inquiry',
        customComponentName: 'RunTestInquiryStep',
        minutesToComplete: 3,
      },
      {
        key: OnboardingGuideStepKey.ReviewCompletedInquiry,
        title: 'View the completed Inquiry',
        customComponentName: 'ReviewCompletedInquiryStep',
        minutesToComplete: 2,
      },
    ],
  },
  {
    moduleKey: OnboardingGuideModuleKey.RefineSolution,
    moduleTitle: 'Refine your solution',
    moduleIcon: <EditIcon />,
    steps: [
      {
        key: OnboardingGuideStepKey.InquiryTemplateBranding,
        title: 'Customize your Inquiry Template',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        customComponentName: null,
        minutesToComplete: 3,
        content: (
          <>
            <Paragraph color='primary'>
              Customize your identity flow with our visual no-code Flow Editor — from the branding
              and copy of each screen, to the decision logic that powers your user experience. Watch
              this video for a walkthrough of the Flow Editor.
            </Paragraph>
            <Spacer height={2} />
            <VideoPlayer
              src='https://player.vimeo.com/video/857255775'
              title='Persona Flow Editor Overview'
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
              allowFullScreen
            />
            <Spacer height={2} />
            <GuideStepTipBanner>
              <Paragraph color='primary'>
                Tip: See our Help Center to learn more about configuring your Inquiry Template:
              </Paragraph>
              <UnorderedList>
                <li>
                  <a
                    href='https://support.withpersona.com/hc/en-us/articles/7223385740435'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Configuring your Inquiry Template
                  </a>
                </li>
              </UnorderedList>
            </GuideStepTipBanner>
          </>
        ),
        buttonText: 'Explore Flow Editor',
        buttonClickCompletesStep: true,
        buttonLink: DashboardPageLink.InquiryTemplateFlow,
      },
      {
        key: OnboardingGuideStepKey.VerificationChecks,
        title: 'Configure Verification checks',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        customComponentName: null,
        minutesToComplete: 3,
        content: (
          <>
            <Paragraph color='primary'>
              A Verification is a series of checks run against the information submitted in an
              Inquiry. Each type of Verification — like Government ID, Selfie, or Database — has its
              own set of checks. You can decide which of these checks must pass in order for the
              Verification to pass.
            </Paragraph>
            <Spacer height={2} />
            <Paragraph color='primary'>
              Depending on your needs, you can require more or fewer checks. This lets you balance
              risk versus conversion for your business.
            </Paragraph>
            <Spacer height={2} />
            <GuideStepGif src={ConfigureVerificationChecks} />
            <Spacer height={2} />
            <GuideStepTipBanner>
              <Paragraph color='primary'>
                Tip: See our Help Center to learn more about Verifications:
              </Paragraph>
              <UnorderedList>
                <li>
                  <a
                    href='https://support.withpersona.com/hc/en-us/articles/14876575884691-Required-and-non-required-Verification-checks'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Required and non-required Verification checks
                  </a>
                </li>
                <li>
                  Example of how to{' '}
                  <a
                    href='https://support.withpersona.com/hc/en-us/articles/14732910792467-Choose-and-configure-Verification-checks-for-Government-ID-Verification'
                    target='_blank'
                    rel='noreferrer'
                  >
                    choose and configure Verification checks
                  </a>
                </li>
              </UnorderedList>
            </GuideStepTipBanner>
          </>
        ),
        buttonText: 'Explore Verification checks',
        buttonClickCompletesStep: true,
        buttonLink: DashboardPageLink.InquiryTemplateVerificationChecks,
      },
      {
        key: OnboardingGuideStepKey.InquiryTemplateCountriesIdTypes,
        title: 'Configure countries and ID types',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        customComponentName: null,
        minutesToComplete: 3,
        content: (
          <>
            <Paragraph color='primary'>
              You can also configure which countries and ID types to enable. This benefits
              businesses that operate within a selected number of countries and/or have specific ID
              requirements.
            </Paragraph>
            <Spacer height={2} />
            <GuideStepGif src={ConfigureIdTypes} />
          </>
        ),
        buttonText: 'Configure countries and ID types',
        buttonClickCompletesStep: true,
        buttonLink: DashboardPageLink.InquiryTemplateVerificationChecks,
      },
    ],
  },
  {
    moduleKey: OnboardingGuideModuleKey.Integration,
    moduleTitle: 'Integrate with your product',
    moduleIcon: <ZapIcon />,
    steps: [
      {
        key: OnboardingGuideStepKey.ChooseIntegrationMethod,
        title: 'Choose how to integrate',
        customComponentName: 'ChooseIntegrationMethodStep',
        minutesToComplete: 1,
      },
      {
        key: OnboardingGuideStepKey.WebhooksOrApi,
        title: 'Learn how to leverage Persona’s webhooks and API',
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        customComponentName: null,
        minutesToComplete: 10,
        content: (
          <>
            <Paragraph color='primary'>
              You can use Persona’s webhooks and APIs to help you further automate your identity
              processes.
            </Paragraph>
            <Spacer height={2} />
            <Paragraph color='primary'>
              For example, if a user finishes an Inquiry and passes all required Verifications, you
              may want to advance them right away onto a next stage in your product. You can set up
              a webhook to alert your systems when a user successfully completes an Inquiry. Then
              your systems can take action.
            </Paragraph>
            <Spacer height={2} />
            <GuideStepTipBanner>
              <Paragraph color='primary'>
                Tip: See our developer docs to learn more about webhooks and the API:
              </Paragraph>
              <UnorderedList>
                <li>
                  <a
                    href='https://docs.withpersona.com/docs/webhooks'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Webhooks overview
                  </a>
                </li>
                <li>
                  <a
                    href='https://docs.withpersona.com/docs/getting-an-api-key'
                    target='_blank'
                    rel='noreferrer'
                  >
                    Getting an API key
                  </a>
                </li>
                <li>
                  <a
                    href='https://docs.withpersona.com/reference/introduction'
                    target='_blank'
                    rel='noreferrer'
                  >
                    API reference
                  </a>
                </li>
              </UnorderedList>
            </GuideStepTipBanner>
          </>
        ),
        buttonText: 'Learn more',
        buttonClickCompletesStep: true,
        buttonLink: DashboardPageLink.Webhooks,
      },
      {
        key: OnboardingGuideStepKey.InviteTeamMembers,
        title: 'Invite team members',
        customComponentName: 'InviteTeamMembersStep',
        minutesToComplete: 1,
      },
    ],
  },
  {
    moduleKey: OnboardingGuideModuleKey.ManageUserLifecycle,
    moduleTitle: '(Optional) Manage user lifecycle',
    moduleIcon: <MonitorIcon />,
    steps: [
      {
        key: OnboardingGuideStepKey.ReviewWorkflowRun,
        title: 'Automate decisioning logic with Workflows',
        customComponentName: 'ReviewWorkflowRunStep',
        minutesToComplete: 2,
        requiredBlueprintBlockType: BlueprintBlockType.Workflow,
      },
      {
        key: OnboardingGuideStepKey.ReviewReportResults,
        title: 'Enrich user data with Reports',
        customComponentName: 'ReviewReportResultsStep',
        minutesToComplete: 2,
        requiredBlueprintBlockType: BlueprintBlockType.ReportTemplate,
      },
      {
        key: OnboardingGuideStepKey.ReviewCase,
        title: 'Try our manual review hub with Cases',
        customComponentName: 'ReviewCaseStep',
        minutesToComplete: 2,
        requiredBlueprintBlockType: BlueprintBlockType.CaseTemplate,
      },
    ],
  },
  {
    moduleKey: OnboardingGuideModuleKey.RequestProductionAccess,
    moduleTitle: 'Test with live data in Production',
    moduleIcon: <StarIcon />,
    steps: [
      {
        key: OnboardingGuideStepKey.RequestProductionAccess,
        title: 'Request Production access',
        customComponentName: 'RequestProductionAccessStep',
        minutesToComplete: 5,
      },
      {
        key: OnboardingGuideStepKey.RunTestInquiryInProduction,
        title: 'Run an Inquiry in Production',
        customComponentName: 'RunTestInquiryInProductionStep',
        minutesToComplete: 3,
      },
    ],
  },
];
